import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Event,
  CalendarMonth,
  Class,
  MonetizationOn,
  Money,
  AdminPanelSettings,
  Download,
  Notes,
} from "@mui/icons-material";
import { downloadBooksFeeReceipt } from "../../../../ApiService/ApiService";
import { ClockIcon } from "@mui/x-date-pickers";
import { formatDateOutput } from "../../../../Utils/formatDate";
import "../../../../Styles/dashboard.css";
import CustomDialog from "../../../../Components/CustomDialog";

const headCells = [
  {
    id: "sessionName",
    label: "Session Name",
    icon: <CalendarMonth sx={{ marginRight: 1 }} />,
  },
  {
    id: "details",
    label: "Details",
    icon: <Notes sx={{ marginRight: 1 }} />,
  },
  { id: "class", label: "Class", icon: <Class sx={{ marginRight: 1 }} /> },
  {
    id: "fromDate",
    label: "From",
    icon: <Event sx={{ marginRight: 1 }} />,
  },
  { id: "toDate", label: "To", icon: <Event sx={{ marginRight: 1 }} /> },
  {
    id: "amount",
    label: "Amount",
    icon: <MonetizationOn sx={{ marginRight: 1 }} />,
  },
  {
    id: "paidOn",
    label: "Paid On",
    icon: <ClockIcon sx={{ marginRight: 1 }} />,
  },
  {
    id: "paymentMode",
    label: "Payment Mode",
    icon: <Money sx={{ marginRight: 1 }} />,
  },
  {
    id: "action",
    label: "Action",
    icon: <AdminPanelSettings sx={{ marginRight: 1 }} />,
  },
];

function BooksFeeList({ bookFeeHistory }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [paginatedData, setPaginatedData] = useState([]);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(bookFeeHistory?.data);
  }, [bookFeeHistory]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleDownloadReceipt = async (feeId) => {
    try {
      const response = await downloadBooksFeeReceipt(feeId);
      if (response) {
        if (response?.data?.status === 200) {
          const pdfUrl = response?.data?.data;
          if (typeof pdfUrl === "string" && pdfUrl.trim() !== "") {
            window.open(pdfUrl, "_blank");
            setDialog({
              visible: true,
              title: "Success",
              message: "Receipt downloaded",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
          } else {
            setDialog({
              visible: true,
              title: "Error",
              message: "Failed to download receipt",
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          const errorMessage =
            response?.data?.errorMessage || "Something went wrong";
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Error",
          message: "Invalid response from the server",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  console.log(bookFeeHistory);

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align="left" padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell className="table-cell" align="center">
                        {row?.academicSession?.name}
                      </TableCell>
                      <TableCell className="table-cell" align="center">
                        {row?.details}
                      </TableCell>
                      <TableCell className="table-cell">
                        {`${row?.studentClass?.grade} ${
                          row?.studentClass?.section
                            ? row?.studentClass?.section
                            : ""
                        }`}
                      </TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.fromDate)}
                      </TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.toDate)}
                      </TableCell>
                      <TableCell className="table-cell" align="center">
                        {parseFloat(row?.amount).toFixed(2)}
                      </TableCell>
                      <TableCell className="table-cell">
                        {row?.paidOn
                          ? formatDateOutput(row?.paidOn)
                          : "Not Paid Yet"}
                      </TableCell>
                      <TableCell className="table-cell" align="center">
                        {row?.paymentMode ? row?.paymentMode : "Not Paid Yet"}
                      </TableCell>
                      <TableCell className="table-cell">
                        <IconButton
                          variant="outlined"
                          onClick={() => handleDownloadReceipt(row?.id)}
                          size="small"
                          sx={{
                            textTransform: "unset",
                            borderRadius: 5,
                          }}
                        >
                          <Download />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

BooksFeeList.propTypes = {
  bookFeeHistory: PropTypes.object.isRequired,
  currSessionName: PropTypes.string.isRequired,
};

export default BooksFeeList;
